import { type MaintenanceProtocolSignatureKind } from '@gb-ticket/shared-types';
import { useState } from 'react';
import { toast } from 'sonner';

import { Button } from '@/components/ui/button';
import { ApiRequestError } from '@/lib/api';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useVoiceCopy } from '@/platform/voice/use-voice';
import { SignaturePad } from '@/products/maintenance/features/protocol/signature-pad';
import {
  uploadMaintenanceProtocolSignature,
  useMaintenanceProtocol,
} from '@/products/maintenance/features/protocol/use-maintenance-protocol';

type ProtocolSignaturesSectionProps = {
  protocolId: string;
  requireTechnicianSignature: boolean;
  requireCustomerSignature: boolean;
  disabled: boolean;
  existing: {
    kind: MaintenanceProtocolSignatureKind;
    signerName: string;
    signerRole?: string | null;
  }[];
};

export function ProtocolSignaturesSection({
  protocolId,
  requireTechnicianSignature,
  requireCustomerSignature,
  disabled,
  existing,
}: ProtocolSignaturesSectionProps) {
  const copy = useVoiceCopy();
  const protocol = useMaintenanceProtocol(protocolId);
  const [consent, setConsent] = useState(false);
  const [customerName, setCustomerName] = useState('');
  const [customerRole, setCustomerRole] = useState('');
  const [techFile, setTechFile] = useState<File | null>(null);
  const [customerFile, setCustomerFile] = useState<File | null>(null);
  const [saving, setSaving] = useState(false);

  const technician = existing.find((s) => s.kind === 'technician');
  const customer = existing.find((s) => s.kind === 'customer');

  async function save(kind: MaintenanceProtocolSignatureKind, file: File | null) {
    if (!consent) {
      toast.error(copy('maintenance.signature.consent.required'));
      return;
    }
    if (!file) {
      toast.error(
        kind === 'customer'
          ? 'Die Unterschrift des Kunden fehlt.'
          : 'Die Unterschrift des Monteurs fehlt.',
      );
      return;
    }
    if (kind === 'customer' && !customerName.trim()) {
      toast.error(copy('maintenance.signature.name.required'));
      return;
    }
    setSaving(true);
    try {
      await uploadMaintenanceProtocolSignature(protocolId, file, {
        kind,
        consentConfirmed: true,
        signerName: kind === 'customer' ? customerName : undefined,
        signerRole: kind === 'customer' ? customerRole : undefined,
      });
      toast.success('Unterschrift gespeichert.');
      await protocol.refetch();
    } catch (error: unknown) {
      toast.error(
        error instanceof ApiRequestError
          ? error.message
          : 'Unterschrift konnte nicht gespeichert werden.',
      );
    } finally {
      setSaving(false);
    }
  }

  return (
    <section className="space-y-4">
      <h2 className="text-lg font-semibold">Unterschriften</h2>
      <p className="text-sm text-muted-foreground">
        {copy('maintenance.signature.confirm-accuracy')}
      </p>
      <div className="flex items-start gap-2">
        <Checkbox
          id="signature-consent"
          checked={consent}
          disabled={disabled}
          onCheckedChange={(v) => setConsent(v === true)}
        />
        <Label htmlFor="signature-consent" className="text-sm leading-snug">
          Ich bestätige, dass die Protokolldaten vollständig und wahrheitsgemäß sind und mit dem
          verbindlichen Abschluss das Protokoll eingereicht wird.
        </Label>
      </div>

      {requireTechnicianSignature ? (
        !technician ? (
          <div className="space-y-2">
            <SignaturePad
              label="Monteur-Unterschrift"
              disabled={disabled || saving}
              onChange={setTechFile}
            />
            <Button
              type="button"
              className="min-h-11 w-full"
              disabled={disabled || saving || !techFile}
              onClick={() => void save('technician', techFile)}
            >
              Monteur-Unterschrift speichern
            </Button>
          </div>
        ) : (
          <p className="text-sm text-green-700">
            Monteur-Unterschrift liegt vor ({technician.signerName}).
          </p>
        )
      ) : (
        <p className="text-sm text-muted-foreground">
          Monteur-Unterschrift ist nicht erforderlich.
        </p>
      )}

      {requireCustomerSignature ? (
        !customer ? (
          <div className="space-y-2">
            <div className="space-y-1">
              <Label htmlFor="customer-name">Name der unterschreibenden Person</Label>
              <Input
                id="customer-name"
                className="min-h-11"
                value={customerName}
                disabled={disabled}
                onChange={(e) => setCustomerName(e.target.value)}
              />
            </div>
            <div className="space-y-1">
              <Label htmlFor="customer-role">Funktion</Label>
              <Input
                id="customer-role"
                className="min-h-11"
                placeholder="z. B. Kunde, Beauftragter, Ansprechpartner"
                value={customerRole}
                disabled={disabled}
                onChange={(e) => setCustomerRole(e.target.value)}
              />
            </div>
            <SignaturePad
              label="Unterschrift vor Ort"
              disabled={disabled || saving}
              onChange={setCustomerFile}
            />
            <Button
              type="button"
              className="min-h-11 w-full"
              disabled={disabled || saving || !customerFile || !customerName.trim()}
              onClick={() => void save('customer', customerFile)}
            >
              Unterschrift speichern
            </Button>
          </div>
        ) : (
          <p className="text-sm text-green-700">
            Unterschrift vor Ort liegt vor ({customer.signerName}
            {customer.signerRole ? `, ${customer.signerRole}` : ''}).
          </p>
        )
      ) : (
        <p className="text-sm text-muted-foreground">
          Unterschrift vor Ort ist nicht erforderlich.
        </p>
      )}
    </section>
  );
}
